import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  IconLayoutDashboard, IconUsers, IconLogout,
  IconSearch, IconRefresh, IconUserCheck, IconUserX,
  IconBell, IconSettings, IconUser, IconHelp,
  IconChevronDown, IconStar, IconX, IconMenu2, IconShield, IconDeviceFloppy, IconMail,
  IconLayoutList, IconEye, IconCalendar, IconChevronLeft, IconChevronRight, IconCalendarEvent,
  IconCamera, IconTrash, IconLogin, IconHistory, IconSpeakerphone, IconAlertTriangle,
  IconAdjustments, IconFolder, IconKey, IconShieldLock, IconDatabaseExport, IconLock,
  IconGolf, IconFlag, IconBuildingBank, IconChartBar, IconDatabase,
} from '@tabler/icons-react';
import api from '../api/api';
import DOMPurify from 'dompurify';
import ThemeSettings from '../components/ThemeSettings';
import ProfileModal from '../components/ProfileModal';
import RolePage from './RolePage';
import MenuPage from './MenuPage';
import MessagePage from './MessagePage';
import BoardManagePage from './BoardManagePage';
import BoardPage from './BoardPage';
import AccountSettingsPage from './AccountSettingsPage';
import SchedulePage from './SchedulePage';
import AuditLogPage from './AuditLogPage';
import NotificationPage from './NotificationPage';
import SystemConfigPage from './SystemConfigPage';
import FileManagerPage from './FileManagerPage';
import ApiKeyManagePage from './ApiKeyManagePage';
import GolfCoursesPage from './GolfCoursesPage';
import GolfReservationsPage from './GolfReservationsPage';
import StockLedgerPage from './StockLedgerPage';
import MarketAnalysisPage from './MarketAnalysisPage';
import KrxDataPage from './KrxDataPage';
import AccessControlPage from './AccessControlPage';
import BackupPage from './BackupPage';
import FrontMenuPage from './FrontMenuPage';
import { onBackdropMouseDown, onBackdropClick } from '../utils/modalDrag';

const USER_WEB_URL = process.env.REACT_APP_USER_WEB_URL || 'http://localhost:3011';

/* 아이콘 코드 → 컴포넌트 매핑 (DB icon 필드용) */
const ICON_MAP = {
  IconLayoutDashboard, IconUsers, IconShield, IconMenu2, IconMail,
  IconLayoutList, IconCalendarEvent, IconHistory, IconSpeakerphone,
  IconFolder, IconKey, IconShieldLock, IconDatabaseExport, IconSettings,
  IconAdjustments, IconGolf, IconLock, IconBuildingBank, IconChartBar, IconDatabase,
};

/* 메뉴 코드 → 기본 아이콘 매핑 (DB icon이 없을 때 fallback) */
const DEFAULT_MENU_ICONS = {
  overview: IconLayoutDashboard, users: IconUsers, roles: IconShield,
  menus: IconMenu2, 'front-menus': IconMenu2, messages: IconMail,
  'board-manage': IconLayoutList, schedules: IconCalendarEvent,
  'audit-logs': IconHistory, notifications: IconSpeakerphone,
  files: IconFolder, 'api-keys': IconKey, 'access-controls': IconShieldLock,
  backups: IconDatabaseExport, 'account-settings': IconSettings,
  'system-config': IconAdjustments, 'golf-courses': IconGolf,
  'golf-reservations': IconCalendarEvent,
  'stock-ledger': IconChartBar, 'market-analysis': IconChartBar, 'krx-data': IconDatabase,
};

/* 기본 라벨 (DB 로드 전 fallback) */
const DEFAULT_LABELS = {
  overview: '대시보드', users: '사용자 관리', roles: '권한 관리',
  menus: '메뉴 관리', 'front-menus': 'FRONT메뉴', messages: '쪽지함',
  'board-manage': '게시판 관리', schedules: '일정관리', 'audit-logs': '활동 로그',
  notifications: '공지/알림', files: '파일 관리', 'api-keys': 'API 키 관리',
  'access-controls': '접근 제어', backups: '백업/복원', 'account-settings': '계정 설정',
  'system-config': '사이트 설정', 'golf-courses': '골프장 관리',
  'golf-reservations': '예약 관리', 'stock-ledger': '주식장부', 'market-analysis': '시장분석',
  'krx-data': 'KRX 데이터',
};

export default function Dashboard() {
  const [tabs, setTabs]           = useState([{ id: 'overview', label: DEFAULT_LABELS.overview, icon: IconLayoutDashboard, closable: false }]);
  const [activeTab, setActiveTab] = useState('overview');
  const [mountedTabs, setMountedTabs] = useState(new Set(['overview']));

  const switchTab = (tabId) => {
    setActiveTab(tabId);
    setMountedTabs(prev => {
      if (prev.has(tabId)) return prev;
      const next = new Set(prev);
      next.add(tabId);
      return next;
    });
  };
  const [navOpen, setNavOpen]     = useState(false);   // 모바일 햄버거

  const [stats, setStats]                   = useState({ totalUsers: 0, activeUsers: 0, blockedUsers: 0, pendingUsers: 0, todayVisitors: 0, totalVisitors: 0 });
  const [users, setUsers]                   = useState([]);
  const [loading, setLoading]               = useState(false);
  const [page, setPage]                     = useState(0);
  const [totalPages, setTotalPages]         = useState(0);
  const [totalElements, setTotalElements]   = useState(0);
  const [keyword, setKeyword]               = useState('');
  const [filterStatus, setFilterStatus]     = useState('');
  const [filterRole, setFilterRole]         = useState('');
  const [toast, setToast]                   = useState(null);
  const [siteConfig, setSiteConfig]         = useState({ 'site.name': 'AI65 Neo' });
  const [isDark, setIsDark]                 = useState(() =>
    document.documentElement.getAttribute('data-bs-theme') === 'dark'
  );

  const [me, setMe]                         = useState(null);
  const [profileOpen, setProfileOpen]       = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const profileRef = useRef(null);

  /* 탭 드래그 순서 변경 */
  const dragTabRef  = useRef(null);   // 드래그 중인 탭 id
  const [dragOverTab, setDragOverTab] = useState(null); // 드롭 대상 탭 id

  /* 인라인 편집: { [userId]: { name?, role?, status? } } */
  const [editedUsers, setEditedUsers] = useState({});
  const [saving, setSaving]           = useState(false);

  /* 허용된 메뉴 코드 목록 (GET /menus/my) */
  const [allowedMenuCodes, setAllowedMenuCodes] = useState([]);
  /* DB 기반 메뉴 데이터 */
  const [myMenus, setMyMenus] = useState([]);           // GET /menus/my 전체 데이터
  const [menuGroups, setMenuGroups] = useState([]);      // GET /menus/groups

  /* MASTER 여부 */
  const isMaster = me?.role === 'MASTER';

  /* 안읽은 쪽지 개수 */
  const [unreadCount, setUnreadCount] = useState(0);

  /* 게시판 */
  const [boards, setBoards]               = useState([]);
  const [openBoards, setOpenBoards]       = useState({}); // { boardId: boardObj }

  /* 상단 메뉴 드롭다운 (동적) */
  const [openDropdown, setOpenDropdown] = useState(null);    // 열려있는 그룹 code
  const dropdownRefs = useRef({});

  /* 대시보드 로그인 사용자 */
  const today = new Date().toLocaleDateString('sv');
  const [loginDate, setLoginDate]           = useState(today);
  const [loginUsers, setLoginUsers]         = useState([]);
  const [loginPage, setLoginPage]           = useState(0);
  const [loginTotalPages, setLoginTotalPages] = useState(0);
  const [loginTotal, setLoginTotal]         = useState(0);

  /* 대시보드 게시판 미리보기 */
  const [dashboardBoards, setDashboardBoards] = useState([]); // [{id,name,listType,posts:[]}]

  /* 대시보드 일정 달력 위젯 */
  const [calYear, setCalYear]           = useState(new Date().getFullYear());
  const [calMonth, setCalMonth]         = useState(new Date().getMonth() + 1);
  const [calSchedules, setCalSchedules] = useState([]);
  const [calBookings, setCalBookings]   = useState([]);
  const [calSelectedDay, setCalSelectedDay] = useState(null);

  /* 팝업 */
  const [popups, setPopups]               = useState([]); // [{ board, post }]
  const [closedPopups, setClosedPopups]   = useState(new Set()); // 현재 세션 닫은 팝업 postId

  /* 사용자 상세 모달 (MASTER 전용) */
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [userDetailTarget, setUserDetailTarget] = useState(null);
  const [userDetailForm, setUserDetailForm] = useState({ name: '', nickname: '', password: '', profileImage: null });
  const [userDetailSaving, setUserDetailSaving] = useState(false);
  const [impersonateLoading, setImpersonateLoading] = useState(false);

  /* 공지/알림 배너 & 팝업 */
  const [activeNotifications, setActiveNotifications] = useState([]);
  const [dismissedBanners, setDismissedBanners] = useState(new Set());
  // ── 마이그레이션 모달 ──
  const [migrateOpen, setMigrateOpen] = useState(false);
  const [migrateChecked, setMigrateChecked] = useState({ courses: true, sections: true, reservations: true, scores: true });
  const [migrateResults, setMigrateResults] = useState({});
  const [migrateRunning, setMigrateRunning] = useState(false);

  const MIGRATE_ITEMS = [
    { key: 'courses', label: '골프장 (jp.golf_place → golf_courses)' },
    { key: 'sections', label: '코스/섹션 (jp.golf_course → golf_course_sections)' },
    { key: 'reservations', label: '예약 (jp.golf_rounding → golf_reservations + golf_reservation_sections)' },
    { key: 'scores', label: '스코어 (jp.golf_rounding_user → golf_round_scores)' },
  ];

  const runMigration = async (mode) => {
    const items = Object.entries(migrateChecked).filter(([, v]) => v).map(([k]) => k);
    if (items.length === 0) return;
    if (mode === 'full') {
      if (!window.confirm('⚠️ 일괄 마이그레이션은 선택한 테이블을 TRUNCATE 후 전체 재이관합니다.\n신규 시스템에서 직접 입력한 데이터도 삭제됩니다.\n정말 실행하시겠습니까?')) return;
    }
    setMigrateRunning(true);
    setMigrateResults({});
    for (const item of items) {
      setMigrateResults(prev => ({ ...prev, [item]: { running: true } }));
      try {
        const res = await api.post(`/admin/golf/migrate/${mode}`, { items: [item] });
        const r = (res.data || [])[0] || {};
        setMigrateResults(prev => ({ ...prev, [item]: r }));
      } catch (e) {
        setMigrateResults(prev => ({ ...prev, [item]: { error: e.response?.data?.message || e.message } }));
      }
    }
    setMigrateRunning(false);
  };

  const [notiPopupOpen, setNotiPopupOpen] = useState(null);       // 현재 표시 중인 팝업 알림
  const [notiDismissedPopups, setNotiDismissedPopups] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('dismissedNotiPopups') || '{}');
      const today = new Date().toLocaleDateString('sv');
      // 오늘 날짜와 다른 항목은 제거
      const cleaned = {};
      Object.entries(stored).forEach(([k, v]) => { if (v === today) cleaned[k] = v; });
      return cleaned;
    } catch { return {}; }
  });

  /* 에디터 타입 (AccountSettingsPage에서 변경 시 전체 반영) */
  const [editorType, setEditorType] = useState(
    () => localStorage.getItem('preferredEditor') || 'jodit'
  );
  const handleEditorTypeChange = (type) => {
    setEditorType(type);
    localStorage.setItem('preferredEditor', type);
  };

  /* 테마 변경 감지 (다크/라이트) */
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.getAttribute('data-bs-theme') === 'dark');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-bs-theme'] });
    return () => observer.disconnect();
  }, []);

  /* 외부 클릭 시 드롭다운 닫기 */
  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
      // 동적 드롭다운: 열린 드롭다운의 ref 밖 클릭 시 닫기
      if (openDropdown) {
        const ref = dropdownRefs.current[openDropdown];
        if (ref && !ref.contains(e.target)) {
          setOpenDropdown(null);
        }
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openDropdown]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchMe = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setMe(res.data);
    } catch {}
  }, []);

  const fetchMyMenus = useCallback(async () => {
    try {
      const res = await api.get('/menus/my');
      setAllowedMenuCodes(res.data.map(m => m.code));
      setMyMenus(res.data);
    } catch {}
  }, []);

  const fetchMenuGroups = useCallback(async () => {
    try {
      const res = await api.get('/menus/groups');
      setMenuGroups(res.data);
    } catch {}
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get('/messages/unread-count');
      setUnreadCount(res.data.count);
    } catch {}
  }, []);

  const fetchBoards = useCallback(async () => {
    try {
      const res = await api.get('/boards');
      setBoards(res.data);
    } catch {}
  }, []);

  const fetchPopupPosts = useCallback(async (boardList) => {
    try {
      const res = await api.get('/posts/popup');
      const popupPosts = res.data; // [{ id, boardId, title, content, ... }]
      if (!popupPosts.length) return;
      const today = new Date().toLocaleDateString('sv');
      // boardId → board 매핑
      const boardMap = Object.fromEntries((boardList || []).map(b => [b.id, b]));
      const pending = [];
      for (const post of popupPosts) {
        const key = `popup_dismissed_${post.id}`;
        if (localStorage.getItem(key) === today) continue;
        const board = boardMap[post.boardId];
        if (board) pending.push({ board, post });
      }
      setPopups(pending);
    } catch {}
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/stats');
      setStats(res.data);
    } catch {}
  }, []);

  const fetchLoginUsers = useCallback(async (date, p = 0) => {
    try {
      const res = await api.get('/admin/login-users', { params: { date, page: p, size: 5 } });
      setLoginUsers(res.data.users);
      setLoginTotalPages(res.data.totalPages);
      setLoginTotal(res.data.totalElements);
      setLoginPage(p);
    } catch {}
  }, []);

  const fetchDashboardBoards = useCallback(async () => {
    try {
      const res = await api.get('/admin/dashboard-posts');
      setDashboardBoards(res.data);
    } catch {}
  }, []);

  const fetchCalSchedules = useCallback(async (y, m) => {
    try {
      const res = await api.get(`/schedules?year=${y}&month=${m}`);
      setCalSchedules(res.data);
    } catch { setCalSchedules([]); }
    try {
      const dateFrom = `${y}-${String(m).padStart(2,'0')}-01`;
      const dateTo = `${y}-${String(m).padStart(2,'0')}-${new Date(y, m, 0).getDate()}`;
      const res = await api.get('/admin/golf/reservations', { params: { dateFrom, dateTo, size: 100, sortField: 'reservedTime', sortDir: 'asc' } });
      setCalBookings(res.data.content || []);
    } catch { setCalBookings([]); }
  }, []);

  const fetchActiveNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications/active');
      const list = res.data || [];
      setActiveNotifications(list);
      // 팝업 타입 중 오늘 dismiss하지 않은 첫 번째를 표시
      const todayStr = new Date().toLocaleDateString('sv');
      const popup = list.find(n => n.type === 'POPUP' && notiDismissedPopups[n.id] !== todayStr);
      if (popup) setNotiPopupOpen(popup);
    } catch { /* ignore */ }
  }, []);

  const fetchUsers = useCallback(async (p = 0, kw = '', status = '', role = '') => {
    setLoading(true);
    try {
      const res = await api.get('/admin/users', {
        params: {
          page: p,
          size: 15,
          keyword: kw || undefined,
          status: status || undefined,
          role: role || undefined,
        },
      });
      setUsers(res.data.users);
      setTotalPages(res.data.totalPages);
      setTotalElements(res.data.totalElements);
      setPage(p);
    } catch {
      showToast('사용자 조회 실패', 'danger');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchMe();
    fetchMyMenus();
    fetchMenuGroups();
    fetchStats();
    fetchUsers(0);
    fetchUnreadCount();
    fetchLoginUsers(today, 0);
    fetchDashboardBoards();
    fetchCalSchedules(new Date().getFullYear(), new Date().getMonth() + 1);
    // boards 로드 후 팝업 포스트 조회
    api.get('/boards').then(res => {
      setBoards(res.data);
      return fetchPopupPosts(res.data);
    }).catch(() => {});
    // 활성 알림 조회
    fetchActiveNotifications();
    // 사이트 설정 조회
    api.get('/system-config').then(res => {
      setSiteConfig(prev => ({ ...prev, ...res.data }));
      if (res.data['site.favicon']) { let l = document.querySelector("link[rel~='icon']"); if (!l) { l = document.createElement('link'); l.rel = 'icon'; document.head.appendChild(l); } l.href = res.data['site.favicon']; }
      if (res.data['site.name']) document.title = res.data['site.name'] + ' - 관리자';
    }).catch(() => {});
    // 30초마다 안읽은 쪽지 개수 폴링
    const timer = setInterval(fetchUnreadCount, 30_000);
    return () => clearInterval(timer);
  }, []);

  /* DB 메뉴 데이터가 로드되면 열려있는 탭 라벨 갱신 */
  useEffect(() => {
    if (myMenus.length === 0) return;
    setTabs(prev => prev.map(tab => {
      const dbMenu = myMenus.find(m => m.code === tab.id);
      if (dbMenu && dbMenu.name && dbMenu.name !== tab.label) {
        return { ...tab, label: dbMenu.name };
      }
      return tab;
    }));
  }, [myMenus]);

  /* DB 메뉴 데이터로 메뉴 아이템 생성 */
  const getMenuItem = useCallback((code) => {
    const dbMenu = myMenus.find(m => m.code === code);
    const label = dbMenu?.name || DEFAULT_LABELS[code] || code;
    const icon = (dbMenu?.icon && ICON_MAP[dbMenu.icon]) || DEFAULT_MENU_ICONS[code] || IconMenu2;
    return { id: code, label, icon, closable: code !== 'overview' };
  }, [myMenus]);

  /* 탭 열기: 없으면 추가, 있으면 활성화 */
  const openTab = (item) => {
    if (!tabs.find(t => t.id === item.id)) {
      setTabs(prev => [...prev, item]);
    }
    switchTab(item.id);
    setNavOpen(false);
    if (item.id === 'users') {
      setKeyword('');
      setFilterStatus('');
      setFilterRole('');
      fetchUsers(0, '', '', '');
    }
    if (item.id === 'roles')    fetchMe();
    if (item.id === 'messages') fetchUnreadCount();
  };

  /* 게시판 탭 열기 */
  const openBoardTab = (board) => {
    const tabId = `board-${board.id}`;
    if (!tabs.find(t => t.id === tabId)) {
      setTabs(prev => [...prev, { id: tabId, label: board.name, icon: IconLayoutList, closable: true }]);
    }
    // initialPostId가 있으면 항상 갱신 (대시보드 글 클릭 → 해당 글 바로 열기)
    setOpenBoards(prev => ({ ...prev, [board.id]: board }));
    switchTab(tabId);
    setNavOpen(false);
    setOpenDropdown(null);
  };

  /* 탭 닫기 */
  const closeTab = (id, e) => {
    e.stopPropagation();
    const next = tabs.filter(t => t.id !== id);
    setTabs(next);
    setMountedTabs(prev => { const n = new Set(prev); n.delete(id); return n; });
    if (activeTab === id) {
      switchTab(next[next.length - 1].id);
    }
  };

  /* 모두 닫기 */
  const closeAllTabs = () => {
    const keep = tabs.filter(t => !t.closable);
    setTabs(keep);
    setMountedTabs(new Set(keep.map(t => t.id)));
    if (!keep.find(t => t.id === activeTab)) {
      switchTab(keep[keep.length - 1].id);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(0, keyword, filterStatus, filterRole);
  };

  /* 인라인 필드 변경 — 즉시 저장하지 않고 편집 상태에만 기록 */
  const updateUserField = (userId, field, value) => {
    const original = users.find(u => u.id === userId);
    if (!original) return;
    setEditedUsers(prev => {
      const prevEdits = prev[userId] || {};
      const newEdits  = { ...prevEdits, [field]: value };
      // 원본과 동일하면 해당 필드 제거
      if (String(original[field] ?? '') === String(value)) {
        delete newEdits[field];
      }
      if (Object.keys(newEdits).length === 0) {
        const { [userId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [userId]: newEdits };
    });
  };

  /* 변경된 사용자 일괄 저장 */
  const handleSaveAll = async () => {
    const changedIds = Object.keys(editedUsers);
    if (changedIds.length === 0) return;
    setSaving(true);
    try {
      await Promise.all(
        changedIds.map(id => api.patch(`/admin/users/${id}`, editedUsers[id]))
      );
      showToast(`${changedIds.length}명 저장되었습니다.`);
      setEditedUsers({});
      fetchUsers(page, keyword, filterStatus, filterRole);
      fetchStats();
    } catch {
      showToast('저장 실패', 'danger');
    } finally {
      setSaving(false);
    }
  };

  /* 사용자 상세 모달 열기 (MASTER 전용) */
  const openUserDetail = (user) => {
    setUserDetailTarget(user);
    setUserDetailForm({ name: user.name || '', nickname: user.nickname || '', password: '', profileImage: user.profileImage || null, color: user.color || '', darkColor: user.darkColor || '' });
    setUserDetailOpen(true);
  };

  const handleUserDetailSave = async () => {
    if (!userDetailTarget) return;
    setUserDetailSaving(true);
    try {
      const payload = {};
      if (userDetailForm.name !== (userDetailTarget.name || '')) payload.name = userDetailForm.name;
      if (userDetailForm.nickname !== (userDetailTarget.nickname || '')) payload.nickname = userDetailForm.nickname;
      if (userDetailForm.password) payload.password = userDetailForm.password;
      if (userDetailForm.profileImage !== (userDetailTarget.profileImage || null)) {
        payload.profileImage = userDetailForm.profileImage || '';
      }
      if (userDetailForm.color !== (userDetailTarget.color || '')) payload.color = userDetailForm.color;
      if (userDetailForm.darkColor !== (userDetailTarget.darkColor || '')) payload.darkColor = userDetailForm.darkColor;
      if (Object.keys(payload).length > 0) {
        await api.patch(`/admin/users/${userDetailTarget.id}/profile`, payload);
        showToast('사용자 정보가 수정되었습니다.');
        fetchUsers(page, keyword, filterStatus, filterRole);
      }
      setUserDetailOpen(false);
    } catch {
      showToast('수정 실패', 'danger');
    } finally {
      setUserDetailSaving(false);
    }
  };

  const handleImpersonate = async (userId, targetUrl = null, newWindow = false) => {
    setImpersonateLoading(true);
    try {
      const res = await api.post(`/admin/users/${userId}/impersonate`);
      const data = res.data;

      if (!data.token || !data.refreshToken) {
        showToast('토큰을 받지 못했습니다. 다시 시도하세요.', 'danger');
        setImpersonateLoading(false);
        return;
      }

      const url = targetUrl || window.location.origin;

      if (newWindow) {
        // 새 창에서 열기: URL에 토큰을 쿼리 파라미터로 전달
        const urlWithToken = `${url}?token=${encodeURIComponent(data.token)}&refreshToken=${encodeURIComponent(data.refreshToken)}`;
        const newTab = window.open(urlWithToken, '_blank');
        if (!newTab) {
          showToast('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.', 'warning');
        }
        setImpersonateLoading(false);
      } else {
        localStorage.setItem('token', data.token);
        localStorage.setItem('refreshToken', data.refreshToken);
        // 약간의 지연 후 이동 (저장 확인)
        setTimeout(() => {
          window.location.href = url;
        }, 100);
      }
    } catch (err) {
      console.error('Impersonate error:', err);
      const errorMsg = err.response?.data?.message || err.response?.statusText || '강제 로그인 실패';
      showToast(errorMsg, 'danger');
      setImpersonateLoading(false);
    }
  };

  /* 팝업 닫기 */
  const closePopup = (postId, neverToday) => {
    if (neverToday) {
      const today = new Date().toLocaleDateString('sv');
      localStorage.setItem(`popup_dismissed_${postId}`, today);
    }
    setClosedPopups(prev => new Set([...prev, postId]));
  };

  /* 팝업에서 게시글 보기 → 해당 게시판 탭 열고 특정 글 상세 표시 */
  const openPopupPost = (board, post) => {
    closePopup(post.id, false);
    const tabId = `board-${board.id}`;
    setOpenBoards(prev => ({ ...prev, [board.id]: { ...board, initialPostId: post.id } }));
    if (!tabs.find(t => t.id === tabId)) {
      setTabs(prev => [...prev, { id: tabId, label: board.name, icon: IconLayoutList, closable: true }]);
    }
    switchTab(tabId);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  const fmt = (val) => val ? val.replace('T', ' ').slice(0, 16) : '-';

  /* 공통 아바타 */
  const Avatar = ({ size = 36, fontSize = 15 }) => (
    me?.profileImage ? (
      <img src={me.profileImage} alt="profile" style={{
        width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
      }} />
    ) : (
      <span style={{
        background: 'var(--tblr-primary, #206bc4)', color: '#fff',
        borderRadius: '50%', width: size, height: size,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize, fontWeight: 700, flexShrink: 0,
      }}>
        {me?.name?.[0]?.toUpperCase() || 'A'}
      </span>
    )
  );

  /* 현재 보여줄 팝업 (세션에서 안 닫은 것) */
  const visiblePopups = popups.filter(p => !closedPopups.has(p.post.id));

  /* 인증 이미지 (갤러리 썸네일용) */
  const DashboardAuthImage = ({ fileId, style, fallback }) => {
    const [blobUrl, setBlobUrl] = React.useState(null);
    React.useEffect(() => {
      if (!fileId) return;
      let objectUrl = null;
      api.get(`/files/${fileId}`, { responseType: 'blob' })
        .then(res => { objectUrl = URL.createObjectURL(res.data); setBlobUrl(objectUrl); })
        .catch(() => {});
      return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [fileId]);
    if (!blobUrl) return fallback || null;
    return <div style={{ ...style, backgroundImage: `url(${blobUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />;
  };

  return (
    <div className="wrapper">

      {/* ── 게시판 팝업 오버레이 ── */}
      {visiblePopups.map(({ board, post }, idx) => (
        <div key={post.id}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 10000 + idx,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => closePopup(post.id, false)}>
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
              width: '100%',
              maxWidth: 600,
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              margin: '0 16px',
            }}
            onClick={e => e.stopPropagation()}>
            {/* 팝업 헤더 */}
            <div style={{
              padding: '14px 20px',
              borderBottom: '1px solid #eee',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#f8f9fa',
            }}>
              <div style={{ cursor: 'pointer' }} onClick={() => openPopupPost(board, post)}>
                <span style={{ fontSize: 11, color: '#6c757d', marginRight: 8 }}>{board.name}</span>
                <span style={{ fontWeight: 700, fontSize: 16 }}>{post.title}</span>
              </div>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                onClick={() => closePopup(post.id, false)}>
                <IconX size={18} />
              </button>
            </div>
            {/* 팝업 본문 */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', fontSize: 14, lineHeight: 1.8 }}>
              <div style={{ color: '#6c757d', fontSize: 12, marginBottom: 12 }}>
                작성자: {post.authorName} · {post.createdAt ? post.createdAt.replace('T', ' ').slice(0, 16) : ''}
              </div>
              <div dangerouslySetInnerHTML={{ __html: (() => {
                const c = post.content || '';
                const t = c.trim();
                if (t.startsWith('<')) return DOMPurify.sanitize(t, { ADD_DATA_URI_TAGS: ['img'] });
                return DOMPurify.sanitize('<p>' + t.replace(/\n/g, '<br>') + '</p>', { ADD_DATA_URI_TAGS: ['img'] });
              })() }} />
            </div>
            {/* 팝업 푸터 */}
            <div style={{
              padding: '10px 20px',
              borderTop: '1px solid #eee',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#f8f9fa',
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: '#6c757d' }}>
                <input type="checkbox" style={{ cursor: 'pointer' }}
                  onChange={e => { if (e.target.checked) closePopup(post.id, true); }} />
                오늘 하루 열지 않기
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-sm btn-primary"
                  onClick={() => openPopupPost(board, post)}>
                  자세히 보기
                </button>
                <button className="btn btn-sm btn-ghost-secondary"
                  onClick={() => closePopup(post.id, false)}>
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* ── Toast 알림 ── */}
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, minWidth: 280 }}>
          <div className={`alert alert-${toast.type} alert-dismissible shadow`} role="alert">
            <div className="d-flex align-items-center">
              {toast.type === 'success'
                ? <IconUserCheck size={18} className="me-2" />
                : <IconUserX size={18} className="me-2" />}
              {toast.msg}
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════
          상단 수평 네비게이션 바  (Tabler style)
          ════════════════════════════════════ */}
      <header className="navbar navbar-expand-md d-print-none">
        <div className="container-xl">

          {/* 모바일 햄버거 */}
          <button
            className="navbar-toggler"
            type="button"
            onClick={() => setNavOpen(v => !v)}
            aria-label="Toggle navigation"
          >
            <IconMenu2 size={20} />
          </button>

          {/* 로고 */}
          <a href="/dashboard" className="navbar-brand navbar-brand-autodark d-none-navbar-horizontal pe-0 pe-md-3 d-flex align-items-center gap-2" style={{ alignSelf: 'stretch' }}>
            {(() => {
              const navLogo = isDark
                ? (siteConfig['site.logo.dark'] || siteConfig['site.logo'])
                : (siteConfig['site.logo.light'] || siteConfig['site.logo']);
              return navLogo
                ? <img src={navLogo} alt="로고" style={{ maxHeight: 30, width: 'auto', borderRadius: 6, objectFit: 'contain' }} />
                : null;
            })()}
            <span className="fw-bold fs-3">{siteConfig['site.name']}</span>
          </a>

          {/* 우측 아이콘/프로필 — order-md-last 로 오른쪽 고정 */}
          <div className="navbar-nav flex-row order-md-last" style={{ position: 'relative' }}>

            {/* DB 마이그레이션 (MASTER만) */}
            {me?.role === 'MASTER' && (
              <div className="nav-item me-1">
                <a className="nav-link px-2" href="#" aria-label="DB 마이그레이션"
                  onClick={(e) => { e.preventDefault(); setMigrateOpen(true); setMigrateResults({}); }}>
                  <IconDatabaseExport size={20} />
                </a>
              </div>
            )}

            {/* 알림 (쪽지함 바로가기 + 안읽은 뱃지) */}
            <div className="nav-item me-1">
              <a className="nav-link px-2 position-relative" href="#" aria-label="쪽지"
                onClick={(e) => { e.preventDefault(); openTab(getMenuItem('messages')); }}>
                <IconBell size={20} />
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: 2, right: 2,
                    background: '#d63939', color: '#fff',
                    borderRadius: '50%', width: 17, height: 17,
                    fontSize: 10, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    lineHeight: 1, border: '2px solid var(--tblr-bg-surface, #fff)',
                  }}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </a>
            </div>

            {/* 프로필 드롭다운 */}
            <div className="nav-item dropdown" ref={profileRef}>
              <a
                className="nav-link d-flex lh-1 text-reset p-0"
                href="#"
                onClick={(e) => { e.preventDefault(); setProfileOpen(v => !v); }}
                style={{ userSelect: 'none', gap: 8 }}
              >
                <Avatar size={36} fontSize={15} />
                <div className="d-none d-xl-block">
                  <div style={{ fontWeight: 600, lineHeight: 1.3 }}>
                    {me?.name || '관리자'}
                    {me?.nickname && <span style={{ fontWeight: 400, opacity: 0.65, marginLeft: 5, fontSize: '0.85em' }}>({me.nickname})</span>}
                  </div>
                  <div className="mt-1" style={{ fontSize: '0.7rem', opacity: 0.6 }}>{me?.email || ''}</div>
                </div>
                <IconChevronDown size={16} className="ms-1 align-self-center" style={{ opacity: 0.6 }} />
              </a>

              {/* 드롭다운 메뉴 */}
              {profileOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 10px)', right: 0,
                  width: 220,
                  background: 'var(--tblr-bg-surface, #fff)',
                  border: '1px solid var(--tblr-border-color, #e6e7e9)',
                  borderRadius: 8,
                  boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                  zIndex: 2000,
                }}>
                  {/* 프로필 헤더 */}
                  <div className="p-3 border-bottom">
                    <div className="d-flex align-items-center gap-2">
                      <Avatar size={40} fontSize={16} />
                      <div className="overflow-hidden">
                        <div className="fw-semibold text-truncate">
                          {me?.name || '관리자'}
                          {me?.nickname && <span className="fw-normal text-muted ms-1" style={{ fontSize: '0.85em' }}>({me.nickname})</span>}
                        </div>
                        <div className="text-muted small text-truncate">{me?.email || ''}</div>
                      </div>
                    </div>
                    <div className="mt-2">
                      <span className="badge bg-primary-lt">
                        <IconStar size={12} className="me-1" />
                        {me?.role || 'ADMIN'}
                      </span>
                    </div>
                  </div>

                  <div className="py-1">
                    <a href="#" className="dropdown-item d-flex align-items-center gap-2 py-2 px-3"
                      onClick={(e) => { e.preventDefault(); setProfileOpen(false); setProfileModalOpen(true); }}>
                      <IconUser size={18} className="text-muted" /> 프로필
                    </a>
                    <a href="#" className="dropdown-item d-flex align-items-center gap-2 py-2 px-3"
                      onClick={(e) => { e.preventDefault(); setProfileOpen(false); openTab(getMenuItem('account-settings')); }}>
                      <IconSettings size={18} className="text-muted" /> 계정 설정
                    </a>
                    <a href="#" className="dropdown-item d-flex align-items-center gap-2 py-2 px-3"
                      onClick={(e) => { e.preventDefault(); setProfileOpen(false); }}>
                      <IconHelp size={18} className="text-muted" /> 도움말
                    </a>
                  </div>

                  <div className="border-top py-1">
                    <a href="#" className="dropdown-item d-flex align-items-center gap-2 py-2 px-3 text-danger"
                      onClick={(e) => { e.preventDefault(); handleLogout(); }}>
                      <IconLogout size={18} /> 로그아웃
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 수평 메뉴 (collapse) — allowedMenuCodes 기반 동적 표시 */}
          <div className={`collapse navbar-collapse${navOpen ? ' show' : ''}`} id="navbar-menu">
            <div className="d-flex flex-column flex-md-row flex-fill align-items-stretch align-items-md-center">
              <ul className="navbar-nav">

                {menuGroups.map(group => {
                  const GroupIcon = ICON_MAP[group.icon] || IconMenu2;
                  // 이 그룹에 속한 메뉴 중 허용된 것만 필터
                  const groupMenus = myMenus
                    .filter(m => m.groupCode === group.code && (m.target === 'ADMIN' || !m.target))
                    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
                  // front-menus는 MASTER만 볼 수 있는 특수 메뉴 (target이 없을 수 있음)
                  if (group.code === 'site' && isMaster) {
                    const hasFrontMenus = groupMenus.find(m => m.code === 'front-menus');
                    if (!hasFrontMenus) {
                      // front-menus가 allowedMenuCodes에 없어도 MASTER는 볼 수 있게
                      const fmIndex = groupMenus.findIndex(m => m.code === 'menus');
                      if (fmIndex >= 0) {
                        groupMenus.splice(fmIndex + 1, 0, { code: 'front-menus', name: DEFAULT_LABELS['front-menus'], icon: 'IconMenu2', sortOrder: 4 });
                      }
                    }
                  }

                  // 대시보드처럼 단일 메뉴인 경우
                  if (group.isSingle) {
                    const singleMenu = groupMenus[0];
                    if (!singleMenu) return null;
                    return (
                      <li key={group.code} className={`nav-item${activeTab === singleMenu.code ? ' active' : ''}`}>
                        <a className="nav-link" href="#"
                          onClick={(e) => { e.preventDefault(); openTab(getMenuItem(singleMenu.code)); }}>
                          <span className="nav-link-icon d-md-none d-lg-inline-block">
                            <GroupIcon size={18} />
                          </span>
                          <span className="nav-link-title">{group.name}</span>
                        </a>
                      </li>
                    );
                  }

                  // 하위 메뉴가 하나도 없으면 그룹 자체를 숨김
                  if (groupMenus.length === 0 && group.code !== 'community') return null;

                  // 현재 활성 탭이 이 그룹에 속하는지
                  const isGroupActive = groupMenus.some(m => m.code === activeTab)
                    || (group.code === 'community' && activeTab.startsWith('board-'));
                  const isOpen = openDropdown === group.code;

                  return (
                    <li key={group.code} className="nav-item"
                      ref={el => { dropdownRefs.current[group.code] = el; }}
                      style={{ position: 'relative' }}>
                      <a className={`nav-link d-flex align-items-center gap-1${isGroupActive ? ' active' : ''}`}
                        href="#"
                        onClick={(e) => { e.preventDefault(); setOpenDropdown(v => v === group.code ? null : group.code); }}>
                        <span className="nav-link-icon d-md-none d-lg-inline-block">
                          <GroupIcon size={18} />
                        </span>
                        <span className="nav-link-title">{group.name}</span>
                        <IconChevronDown size={13} style={{ opacity: 0.6, marginLeft: 2 }} />
                      </a>
                      {isOpen && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0,
                          minWidth: 180,
                          background: 'var(--tblr-bg-surface, #fff)',
                          border: '1px solid var(--tblr-border-color, #e6e7e9)',
                          borderRadius: 6,
                          boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
                          zIndex: 3000,
                        }}>
                          {groupMenus.map(menu => {
                            const MenuIcon = (menu.icon && ICON_MAP[menu.icon]) || DEFAULT_MENU_ICONS[menu.code] || IconMenu2;
                            // 권한 체크: allowedMenuCodes에 포함되거나 MASTER
                            if (!allowedMenuCodes.includes(menu.code) && !isMaster) return null;
                            // files는 ADMIN/MASTER만
                            if (menu.code === 'files' && me?.role !== 'ADMIN' && me?.role !== 'MASTER') return null;
                            return (
                              <button key={menu.code} className="dropdown-item d-flex align-items-center gap-2 py-2 px-3"
                                onClick={() => { setOpenDropdown(null); openTab(getMenuItem(menu.code)); }}>
                                <MenuIcon size={14} className="text-muted" />
                                <span>{menu.name || DEFAULT_LABELS[menu.code]}</span>
                                {menu.code === 'messages' && unreadCount > 0 && (
                                  <span className="badge bg-danger text-white ms-auto" style={{ fontSize: 10 }}>
                                    {unreadCount > 99 ? '99+' : unreadCount}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                          {/* 커뮤니티 그룹: 게시판 목록 추가 */}
                          {group.code === 'community' && boards.length > 0 && (
                            <>
                              <div className="dropdown-divider my-1" />
                              <div className="px-3 py-1" style={{ fontSize: 11, color: '#9aa0ac', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                                게시판
                              </div>
                              {boards.map(b => (
                                <button key={b.id} className="dropdown-item d-flex align-items-center gap-2 py-2 px-3"
                                  onClick={() => { setOpenDropdown(null); openBoardTab(b); }}>
                                  <IconLayoutList size={14} className="text-muted" />
                                  {b.name}
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}

              </ul>
            </div>
          </div>

        </div>
      </header>

      {/* ════════════════════════════════════
          메인 페이지 영역
          ════════════════════════════════════ */}
      <div className="page-wrapper">

        {/* ── 활성 배너 알림 ── */}
        {activeNotifications.filter(n => n.type === 'BANNER' && !dismissedBanners.has(n.id)).map(n => (
          <div key={n.id} style={{
            backgroundColor: n.bgColor || '#e7f3ff',
            color: n.textColor || '#0d6efd',
            padding: '10px 16px',
            fontSize: 14,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            position: 'relative',
          }}>
            <IconBell size={16} />
            {n.linkUrl ? (
              <a href={n.linkUrl} target="_blank" rel="noopener noreferrer"
                style={{ color: n.textColor || '#0d6efd', textDecoration: 'underline' }}>
                {n.title}
              </a>
            ) : (
              <span>{n.title}</span>
            )}
            <button onClick={() => setDismissedBanners(prev => new Set([...prev, n.id]))}
              style={{
                position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer',
                color: n.textColor || '#0d6efd', opacity: 0.7,
              }}>
              <IconX size={16} />
            </button>
          </div>
        ))}

        {/* ── 마이그레이션 모달 ── */}
        {migrateOpen && (
          <>
            <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} />
            <div className="modal modal-blur fade show" style={{ display: 'block', zIndex: 1061 }}
              onMouseDown={onBackdropMouseDown} onClick={(e) => { if (!migrateRunning) onBackdropClick(e, () => setMigrateOpen(false)); }}>
              <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 680 }}>
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title d-flex align-items-center gap-2">
                      <IconDatabaseExport size={20} />
                      일괄 마이그레이션
                    </h5>
                    <button type="button" className="btn-close" disabled={migrateRunning}
                      onClick={() => setMigrateOpen(false)} />
                  </div>
                  <div className="modal-body p-0">
                    <table className="table table-vcenter card-table mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}>
                            <input type="checkbox" className="form-check-input"
                              checked={Object.values(migrateChecked).every(Boolean)}
                              onChange={(e) => {
                                const v = e.target.checked;
                                setMigrateChecked({ courses: v, sections: v, reservations: v, scores: v });
                              }} />
                          </th>
                          <th>항목</th>
                          <th>결과</th>
                        </tr>
                      </thead>
                      <tbody>
                        {MIGRATE_ITEMS.map(item => (
                          <tr key={item.key}>
                            <td>
                              <input type="checkbox" className="form-check-input"
                                checked={migrateChecked[item.key]}
                                onChange={(e) => setMigrateChecked(prev => ({ ...prev, [item.key]: e.target.checked }))} />
                            </td>
                            <td>{item.label}</td>
                            <td>
                              {migrateResults[item.key] ? (
                                migrateResults[item.key].running ? (
                                  <span className="text-muted" style={{ fontSize: 12 }}>
                                    <span className="spinner-border spinner-border-sm me-1" style={{ width: 12, height: 12 }} />
                                    이관 중...
                                  </span>
                                ) : migrateResults[item.key].error ? (
                                  <span className="text-danger" style={{ fontSize: 12 }}>❌ {migrateResults[item.key].error}</span>
                                ) : (
                                  <span className="text-success" style={{ fontSize: 12 }}>
                                    ✅ 전체 {migrateResults[item.key].total}, 이관 {migrateResults[item.key].migrated}, 스킵 {migrateResults[item.key].skipped} ({migrateResults[item.key].seconds}초)
                                  </span>
                                )
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="modal-footer">
                    {migrateResults && Object.keys(migrateResults).length > 0 && !migrateRunning && (
                      <span className="text-muted me-auto" style={{ fontSize: 12 }}>
                        총 실행시간: {Object.values(migrateResults).reduce((s, r) => s + (r.seconds || 0), 0).toFixed(1)}초
                      </span>
                    )}
                    <button className="btn btn-secondary" disabled={migrateRunning}
                      onClick={() => setMigrateOpen(false)}>닫기</button>
                    <button className="btn btn-primary" disabled={migrateRunning}
                      onClick={() => runMigration('incremental')}>
                      {migrateRunning ? <span className="spinner-border spinner-border-sm me-1" /> : <IconDatabaseExport size={16} className="me-1" />}
                      추가 마이그레이션 ({Object.values(migrateChecked).filter(Boolean).length}개)
                    </button>
                    <button className="btn btn-danger" disabled={migrateRunning}
                      onClick={() => runMigration('full')}>
                      {migrateRunning ? <span className="spinner-border spinner-border-sm me-1" /> : <IconDatabaseExport size={16} className="me-1" />}
                      일괄 마이그레이션 ({Object.values(migrateChecked).filter(Boolean).length}개)
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── 팝업 알림 ── */}
        {notiPopupOpen && (
          <>
            <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} />
            <div className="modal modal-blur fade show" style={{ display: 'block', zIndex: 1061 }}
              onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, () => setNotiPopupOpen(null))}>
              <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 500 }}>
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title d-flex align-items-center gap-2">
                      <IconAlertTriangle size={20} className="text-warning" />
                      {notiPopupOpen.title}
                    </h5>
                    <button type="button" className="btn-close" onClick={() => setNotiPopupOpen(null)} />
                  </div>
                  {notiPopupOpen.content && (
                    <div className="modal-body" dangerouslySetInnerHTML={{ __html: notiPopupOpen.content }} />
                  )}
                  <div className="modal-footer justify-content-between">
                    {notiPopupOpen.dismissible && (
                      <button className="btn btn-ghost-secondary btn-sm" onClick={() => {
                        const todayStr = new Date().toLocaleDateString('sv');
                        const updated = { ...notiDismissedPopups, [notiPopupOpen.id]: todayStr };
                        setNotiDismissedPopups(updated);
                        localStorage.setItem('dismissedNotiPopups', JSON.stringify(updated));
                        setNotiPopupOpen(null);
                      }}>오늘 하루 안 보기</button>
                    )}
                    <div className="ms-auto d-flex gap-2">
                      {notiPopupOpen.linkUrl && (
                        <a href={notiPopupOpen.linkUrl} target="_blank" rel="noopener noreferrer"
                          className="btn btn-primary btn-sm">자세히 보기</a>
                      )}
                      <button className="btn btn-secondary btn-sm" onClick={() => setNotiPopupOpen(null)}>닫기</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── 탭 헤더 ── */}
        <div className="page-header d-print-none" style={{ paddingBottom: 0 }}>
          <div className="container-xl">
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 0,
              borderBottom: '2px solid var(--tblr-border-color, #e6e7e9)',
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            }} className="hide-scrollbar">
              {tabs.map(tab => {
                const Icon = tab.icon;
                const isActive  = activeTab === tab.id;
                const isDragOver = dragOverTab === tab.id && dragTabRef.current !== tab.id;
                return (
                  <div
                    key={tab.id}
                    draggable
                    onDragStart={(e) => {
                      dragTabRef.current = tab.id;
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      if (dragOverTab !== tab.id) setDragOverTab(tab.id);
                    }}
                    onDragLeave={() => setDragOverTab(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOverTab(null);
                      const fromId = dragTabRef.current;
                      const toId = tab.id;
                      if (!fromId || fromId === toId) return;
                      setTabs(prev => {
                        const fromIdx = prev.findIndex(t => t.id === fromId);
                        const toIdx   = prev.findIndex(t => t.id === toId);
                        if (fromIdx === -1 || toIdx === -1) return prev;
                        const next = [...prev];
                        const [moved] = next.splice(fromIdx, 1);
                        next.splice(toIdx, 0, moved);
                        return next;
                      });
                    }}
                    onDragEnd={() => { dragTabRef.current = null; setDragOverTab(null); }}
                    onClick={() => switchTab(tab.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '10px 16px',
                      cursor: 'grab',
                      borderBottom: isActive
                        ? '2px solid var(--tblr-primary, #206bc4)'
                        : isDragOver
                          ? '2px solid var(--tblr-primary, #206bc4)'
                          : '2px solid transparent',
                      marginBottom: -2,
                      color: isActive || isDragOver
                        ? 'var(--tblr-primary, #206bc4)'
                        : 'var(--tblr-muted, #9aa0ac)',
                      fontWeight: isActive ? 600 : 400,
                      fontSize: 14, userSelect: 'none',
                      background: isActive
                        ? 'rgba(32,107,196,0.05)'
                        : isDragOver
                          ? 'rgba(32,107,196,0.08)'
                          : 'transparent',
                      borderRadius: '6px 6px 0 0',
                      transition: 'color 0.15s, background 0.15s',
                      whiteSpace: 'nowrap',
                      outline: isDragOver ? '1px dashed rgba(32,107,196,0.4)' : 'none',
                      outlineOffset: '-2px',
                    }}
                  >
                    <Icon size={15} />
                    <span>{tab.label}</span>
                    {tab.closable && (
                      <button
                        onClick={(e) => closeTab(tab.id, e)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          padding: '0 0 0 2px', lineHeight: 1, color: 'inherit',
                          opacity: 0.5, display: 'flex', alignItems: 'center',
                          borderRadius: 3, transition: 'opacity 0.1s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                        onMouseLeave={e => e.currentTarget.style.opacity = '0.5'}
                      >
                        <IconX size={13} />
                      </button>
                    )}
                  </div>
                );
              })}
              {tabs.some(t => t.closable) && (
                <button className="btn btn-sm btn-ghost-secondary d-flex align-items-center gap-1 ms-1 flex-shrink-0"
                  style={{ fontSize: 11, padding: '2px 6px', opacity: 0.6 }}
                  onClick={closeAllTabs} title="모두 닫기">
                  <IconX size={12} /> 모두닫기
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── 대시보드 콘텐츠 ── */}
        <div style={{ display: activeTab === 'overview' ? 'block' : 'none' }}>
          <div className="page-body">
            <div className="container-xl">
              <div className="row row-deck row-cards">

                {/* ── Row1: 일정 달력 위젯 ── */}
                <div className="col-12">
                  <div className="card h-100">
                    <div className="card-header d-flex align-items-center">
                      <button className="btn btn-ghost-secondary btn-sm px-2" onClick={() => {
                        const nm = calMonth === 1 ? 12 : calMonth - 1;
                        const ny = calMonth === 1 ? calYear - 1 : calYear;
                        setCalMonth(nm); setCalYear(ny); setCalSelectedDay(null);
                        fetchCalSchedules(ny, nm);
                      }}>
                        <IconChevronLeft size={16} />
                      </button>
                      <h3 className="card-title mb-0 mx-2" style={{ minWidth: 100, textAlign: 'center', cursor: 'pointer' }}
                        onClick={() => openTab(getMenuItem('schedules'))}>
                        <span className="text-primary">{calYear}년 {calMonth}월</span>
                      </h3>
                      <button className="btn btn-ghost-secondary btn-sm px-2" onClick={() => {
                        const nm = calMonth === 12 ? 1 : calMonth + 1;
                        const ny = calMonth === 12 ? calYear + 1 : calYear;
                        setCalMonth(nm); setCalYear(ny); setCalSelectedDay(null);
                        fetchCalSchedules(ny, nm);
                      }}>
                        <IconChevronRight size={16} />
                      </button>
                    </div>
                    <div className="card-body p-0">
                      {(() => {
                        const DAYS_H = ['일','월','화','수','목','금','토'];
                        const CAL_COLORS = ['#206bc4','#2fb344','#d63939','#f76707','#ae3ec9','#0ca678','#d6336c'];
                        const firstDay = new Date(calYear, calMonth - 1, 1).getDay();
                        const daysInMonth = new Date(calYear, calMonth, 0).getDate();
                        const cells = [];
                        for (let i = 0; i < firstDay; i++) cells.push(null);
                        for (let d = 1; d <= daysInMonth; d++) cells.push(d);
                        while (cells.length % 7 !== 0) cells.push(null);
                        const rows = [];
                        for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
                        const todayD = new Date();
                        const isTodayMonth = todayD.getFullYear() === calYear && todayD.getMonth() + 1 === calMonth;
                        const todayDate = isTodayMonth ? todayD.getDate() : null;
                        const getEvts = (day) => {
                          if (!day) return [];
                          const ds = `${calYear}-${String(calMonth).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                          return calSchedules.filter(s => s.startDate <= ds && s.endDate >= ds);
                        };
                        const getBkgs = (day) => {
                          if (!day) return [];
                          const ds = `${calYear}-${String(calMonth).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                          return calBookings.filter(b => b.reservedDate === ds);
                        };
                        return (
                          <table className="table table-bordered mb-0" style={{ tableLayout: 'fixed' }}>
                            <thead>
                              <tr>
                                {DAYS_H.map((d,i) => (
                                  <th key={d} className="text-center py-1" style={{
                                    fontSize: 12, fontWeight: 600, background: '#f8f9fa',
                                    color: i===0?'#d63939':i===6?'#206bc4':'inherit',
                                  }}>{d}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((row, ri) => (
                                <tr key={ri}>
                                  {row.map((day, ci) => {
                                    const evts = getEvts(day);
                                    const bkgs = getBkgs(day);
                                    const allItems = [
                                      ...evts.map(s => ({ type: 'schedule', data: s })),
                                      ...bkgs.map(b => ({ type: 'booking', data: b })),
                                    ];
                                    const isToday = day === todayDate;
                                    const isSel = day === calSelectedDay;
                                    return (
                                      <td key={ci} style={{
                                        height: 68, verticalAlign: 'top', padding: '2px 4px',
                                        cursor: day ? 'pointer' : 'default',
                                        background: isSel ? '#e8f0fe' : isToday ? '#fffce6' : 'transparent',
                                      }} onClick={() => day && setCalSelectedDay(day === calSelectedDay ? null : day)}>
                                        {day && (
                                          <>
                                            <div style={{
                                              fontSize: 12, fontWeight: isToday ? 700 : 400,
                                              color: ci===0?'#d63939':ci===6?'#206bc4':'inherit',
                                            }}>
                                              {isToday ? (
                                                <span style={{
                                                  background:'#206bc4',color:'#fff',borderRadius:'50%',
                                                  width:20,height:20,display:'inline-flex',
                                                  alignItems:'center',justifyContent:'center',fontSize:11,
                                                }}>{day}</span>
                                              ) : day}
                                            </div>
                                            {allItems.slice(0,2).map((item, idx) => (
                                              item.type === 'schedule' ? (
                                                <div key={`s-${item.data.id}`} style={{
                                                  fontSize:10, padding:'0 3px', marginBottom:1,
                                                  borderRadius:2, color:'#fff',
                                                  background: CAL_COLORS[item.data.id % CAL_COLORS.length],
                                                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                                                }}>{item.data.title}</div>
                                              ) : (
                                                <div key={`b-${item.data.id}`} style={{
                                                  fontSize:10, padding:'0 3px', marginBottom:1,
                                                  borderRadius:2, display:'flex', alignItems:'center', gap:2,
                                                  color: item.data.gameType==='메이저'?'#ae3ec9':'#2fb344',
                                                  background: item.data.gameType==='메이저'?'#f3e8ff':'#e6f9ed',
                                                  overflow:'hidden', whiteSpace:'nowrap',
                                                }}>
                                                  <IconFlag size={8} style={{flexShrink:0}} />
                                                  <span style={{overflow:'hidden',textOverflow:'ellipsis'}}>{item.data.reservedTime?.slice(0,5)} {item.data.golfCourseName}</span>
                                                </div>
                                              )
                                            ))}
                                            {allItems.length > 2 && <div style={{fontSize:9,color:'#888'}}>+{allItems.length-2}</div>}
                                          </>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        );
                      })()}
                      {/* 선택 날짜 일정 */}
                      {calSelectedDay && (() => {
                        const ds = `${calYear}-${String(calMonth).padStart(2,'0')}-${String(calSelectedDay).padStart(2,'0')}`;
                        const evts = calSchedules.filter(s => s.startDate <= ds && s.endDate >= ds);
                        const bkgs = calBookings.filter(b => b.reservedDate === ds);
                        return (
                          <div style={{ padding: '8px 12px', borderTop: '1px solid var(--tblr-border-color)' }}>
                            <div className="d-flex align-items-center mb-1">
                              <strong style={{ fontSize: 13 }}>{calMonth}/{calSelectedDay} 일정</strong>
                              <button className="btn btn-sm btn-ghost-secondary ms-auto px-1" onClick={() => setCalSelectedDay(null)}>
                                <IconX size={14} />
                              </button>
                            </div>
                            {bkgs.map(b => (
                              <div key={`b-${b.id}`} className="py-1" style={{ fontSize: 13 }}>
                                <div className="d-flex align-items-center gap-2">
                                  <IconFlag size={10} color={b.gameType==='메이저'?'#ae3ec9':'#2fb344'} style={{flexShrink:0}} />
                                  <strong style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color: b.gameType==='메이저'?'#ae3ec9':'#2fb344' }}>
                                    {b.reservedTime?.slice(0,5)} {b.golfCourseName}
                                  </strong>
                                  <span className={`badge ms-auto flex-shrink-0 ${b.gameType==='메이저'?'bg-purple-lt':'bg-green-lt'}`} style={{fontSize:9}}>{b.gameType||'일반'}</span>
                                </div>
                                <div className="text-muted small ms-3">{b.playerCount}명{b.reserverName&&` · ${b.reserverName}`}{b.totalHoles&&` · ${b.totalHoles}홀`}</div>
                              </div>
                            ))}
                            {evts.length === 0 && bkgs.length === 0 ? (
                              <div>
                                <div className="text-muted small mb-2">일정이 없습니다</div>
                                <button className="btn btn-sm btn-primary" onClick={() => openTab(getMenuItem('schedules'))}>
                                  <IconCalendarEvent size={14} className="me-1" />일정추가
                                </button>
                              </div>
                            ) : evts.map(s => (
                              <div key={s.id} className="py-1" style={{ fontSize: 13 }}>
                                <div className="d-flex align-items-center gap-2">
                                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#206bc4', flexShrink: 0 }} />
                                  <strong style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.title}</strong>
                                  <span className="text-muted small ms-auto flex-shrink-0">
                                    {s.startDate === s.endDate ? '' : `${s.startDate} ~ ${s.endDate}`}
                                  </span>
                                </div>
                                {s.description && (
                                  <div className="text-muted small ms-3 mt-1" style={{ whiteSpace: 'pre-line', lineHeight: 1.4 }}>
                                    {s.description}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>


                {/* ── Row2: 날짜별 로그인 사용자 ── */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header d-flex align-items-center gap-2" style={{ flexWrap: 'wrap' }}>
                      <div className="d-flex align-items-center gap-2">
                        <IconCalendar size={18} className="text-muted" />
                        <h3 className="card-title mb-0">로그인 사용자</h3>
                        <span className="text-muted small">총 {loginTotal}명</span>
                      </div>
                      <div className="ms-auto d-flex align-items-center gap-1">
                        {/* 이전날 */}
                        <button className="btn btn-sm btn-ghost-secondary px-2 d-none d-md-inline-flex"
                          onClick={() => {
                            const d = new Date(loginDate);
                            d.setDate(d.getDate() - 1);
                            const nd = d.toLocaleDateString('sv');
                            setLoginDate(nd);
                            fetchLoginUsers(nd, 0);
                          }}>
                          <IconChevronLeft size={16} />
                        </button>
                        <input type="date" className="form-control form-control-sm" style={{ width: 130, minWidth: 0 }}
                          value={loginDate}
                          max={today}
                          onChange={e => { setLoginDate(e.target.value); fetchLoginUsers(e.target.value, 0); }}
                        />
                        {/* 다음날 */}
                        <button className="btn btn-sm btn-ghost-secondary px-2 d-none d-md-inline-flex"
                          disabled={loginDate >= today}
                          onClick={() => {
                            const d = new Date(loginDate);
                            d.setDate(d.getDate() + 1);
                            const nd = d.toLocaleDateString('sv');
                            setLoginDate(nd);
                            fetchLoginUsers(nd, 0);
                          }}>
                          <IconChevronRight size={16} />
                        </button>
                      </div>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-vcenter table-hover card-table" style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th>이메일</th>
                            <th className="d-none d-md-table-cell">이름</th>
                            <th>역할</th>
                            <th>상태</th>
                            <th>로그인 시간</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loginUsers.length === 0 ? (
                            <tr><td colSpan={5} className="text-center text-muted py-4">해당 날짜에 로그인한 사용자가 없습니다.</td></tr>
                          ) : loginUsers.map(u => (
                            <tr key={u.id}>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <span style={{
                                    background: '#206bc4', color: '#fff', borderRadius: '50%',
                                    width: 28, height: 28, display: 'inline-flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    fontSize: 12, fontWeight: 600, flexShrink: 0,
                                  }}>
                                    {(u.name || u.email)[0].toUpperCase()}
                                  </span>
                                  {u.email}
                                </div>
                              </td>
                              <td className="text-muted d-none d-md-table-cell">{u.name || '-'}</td>
                              <td>
                                <span className={`badge ${u.role === 'MASTER' ? 'bg-danger' : u.role === 'ADMIN' ? 'bg-purple' : 'bg-azure'} text-white`}>
                                  {u.role}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${u.status === 'ACTIVE' ? 'bg-success' : u.status === 'PENDING' ? 'bg-warning' : 'bg-danger'} text-white`}>
                                  {u.status === 'PENDING' ? '대기' : u.status}
                                </span>
                              </td>
                              <td className="text-muted small">{fmt(u.lastLoginAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* 페이징 */}
                    {loginTotalPages > 1 && (
                      <div className="card-footer d-flex align-items-center justify-content-center gap-1">
                        <button className="btn btn-sm btn-ghost-secondary"
                          disabled={loginPage === 0}
                          onClick={() => fetchLoginUsers(loginDate, loginPage - 1)}>
                          <IconChevronLeft size={15} />
                        </button>
                        {[...Array(loginTotalPages)].map((_, i) => (
                          <button key={i}
                            className={`btn btn-sm ${loginPage === i ? 'btn-primary' : 'btn-ghost-secondary'}`}
                            onClick={() => fetchLoginUsers(loginDate, i)}>
                            {i + 1}
                          </button>
                        ))}
                        <button className="btn btn-sm btn-ghost-secondary"
                          disabled={loginPage >= loginTotalPages - 1}
                          onClick={() => fetchLoginUsers(loginDate, loginPage + 1)}>
                          <IconChevronRight size={15} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Row3: 일반 게시판 미리보기 (LIST/BLOG 타입, 최대 3개) ── */}
                {(() => {
                  const listBoards = dashboardBoards.filter(b => b.listType !== 'GALLERY').slice(0, 3);
                  if (listBoards.length === 0) return null;
                  const colClass = listBoards.length === 1 ? 'col-12' : listBoards.length === 2 ? 'col-md-6' : 'col-md-4';
                  return listBoards.map(b => (
                    <div key={b.id} className={colClass}>
                      <div className="card h-100">
                        <div className="card-header">
                          <h3 className="card-title mb-0" style={{ cursor: 'pointer' }}
                            onClick={() => openBoardTab({ id: b.id, name: b.name, listType: b.listType })}>
                            <span className="text-primary">{b.name}</span>
                          </h3>
                        </div>
                        <div className="list-group list-group-flush">
                          {b.posts.length === 0 ? (
                            <div className="list-group-item text-muted small py-3 text-center">등록된 게시글이 없습니다.</div>
                          ) : b.posts.map(p => (
                            <div key={p.id} className="list-group-item list-group-item-action"
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                openBoardTab({ id: b.id, name: b.name, listType: b.listType, initialPostId: p.id });
                              }}>
                              <div className="d-flex align-items-center justify-content-between gap-2">
                                <span style={{
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 14,
                                  color: p.isNotice ? 'var(--tblr-danger)' : 'inherit',
                                  fontWeight: p.isNotice ? 600 : 400,
                                }}>
                                  {p.isNotice && <span className="badge bg-danger-lt me-1" style={{ fontSize: 10 }}>공지</span>}
                                  {p.title}
                                </span>
                                <span className="text-muted small flex-shrink-0">{fmt(p.createdAt)}</span>
                              </div>
                              <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                                {p.authorName || '-'}
                                <span className="ms-2">
                                  <IconEye size={11} className="me-1" />
                                  {p.viewCount}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ));
                })()}

                {/* ── Row4: 갤러리 게시판 미리보기 ── */}
                {(() => {
                  const galleryBoards = dashboardBoards.filter(b => b.listType === 'GALLERY');
                  if (galleryBoards.length === 0) return null;
                  return galleryBoards.slice(0, 1).map(b => (
                    <div key={b.id} className="col-12">
                      <div className="card">
                        <div className="card-header">
                          <h3 className="card-title mb-0" style={{ cursor: 'pointer' }}
                            onClick={() => openBoardTab({ id: b.id, name: b.name, listType: b.listType })}>
                            <span className="text-primary">{b.name}</span>
                          </h3>
                        </div>
                        <div className="card-body">
                          {b.posts.length === 0 ? (
                            <div className="text-muted text-center py-3">등록된 게시글이 없습니다.</div>
                          ) : (
                            <div className="row g-3">
                              {b.posts.map(p => (
                                <div key={p.id} className="col-6 col-md-3"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => openBoardTab({ id: b.id, name: b.name, listType: b.listType, initialPostId: p.id })}>
                                  {/* 썸네일 */}
                                  <div style={{ width: '100%', paddingTop: '75%', position: 'relative', borderRadius: 6, overflow: 'hidden', background: '#f1f3f4' }}>
                                    {p.thumbnail ? (
                                      <div style={{
                                        position: 'absolute', inset: 0,
                                        backgroundImage: `url(${p.thumbnail})`,
                                        backgroundSize: 'cover', backgroundPosition: 'center',
                                      }} />
                                    ) : p.thumbnailFileId ? (
                                      <DashboardAuthImage
                                        fileId={p.thumbnailFileId}
                                        style={{ position: 'absolute', inset: 0 }}
                                        fallback={
                                          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>🖼️</div>
                                        }
                                      />
                                    ) : (
                                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>🖼️</div>
                                    )}
                                  </div>
                                  {/* 제목 */}
                                  <div style={{ marginTop: 6, fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {p.title}
                                  </div>
                                  <div className="text-muted" style={{ fontSize: 11 }}>
                                    {p.authorName} · {fmt(p.createdAt)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ));
                })()}

              </div>
            </div>
          </div>
        </div>

        {/* ── 사용자 관리 콘텐츠 ── */}
        <div style={{ display: mountedTabs.has('users') ? (activeTab === 'users' ? 'block' : 'none') : 'none' }}>
        {mountedTabs.has('users') && (
          <div className="page-body">
            <div className="container-xl">

              <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <div className="text-muted small">관리</div>
                  <h2 className="mb-0" style={{ fontSize: 20, fontWeight: 700 }}>사용자 목록</h2>
                </div>
                <span className="text-muted">전체 <strong>{totalElements}</strong>명</span>
              </div>

              <div className="card">
                <div className="card-header">
                  <form className="w-100" onSubmit={handleSearch}>
                    <div className="row g-2 align-items-center">
                      {/* 검색어 (좌측 50%) */}
                      <div className="col-12 col-md-6">
                        <div className="input-group input-group-flat">
                          <span className="input-group-text">
                            <IconSearch size={16} className="text-muted" />
                          </span>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="아이디 또는 이름으로 검색..."
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                          />
                        </div>
                      </div>
                      {/* 상태/역할 필터 + 버튼 (우측 50%) */}
                      <div className="col-12 col-md-6">
                    <div className="d-flex gap-2 align-items-center flex-wrap">
                      <select
                        className="form-select form-select-sm"
                        style={{ minWidth: 120, maxWidth: 150 }}
                        value={filterStatus}
                        onChange={(e) => { setFilterStatus(e.target.value); fetchUsers(0, keyword, e.target.value, filterRole); }}
                      >
                        <option value="">상태 전체</option>
                        <option value="ACTIVE">활성</option>
                        <option value="PENDING">대기</option>
                        <option value="BLOCKED">차단</option>
                      </select>

                      <select
                        className="form-select form-select-sm"
                        style={{ minWidth: 120, maxWidth: 150 }}
                        value={filterRole}
                        onChange={(e) => { setFilterRole(e.target.value); fetchUsers(0, keyword, filterStatus, e.target.value); }}
                      >
                        <option value="">역할 전체</option>
                        <option value="MASTER">마스터</option>
                        <option value="ADMIN">관리자</option>
                        <option value="USER">사용자</option>
                      </select>

                      <button type="submit" className="btn btn-sm btn-primary">검색</button>
                      <button type="button" className="btn btn-sm btn-outline-secondary"
                        onClick={() => { setKeyword(''); setFilterStatus(''); setFilterRole(''); setEditedUsers({}); fetchUsers(0, '', '', ''); }}>
                        <IconRefresh size={16} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-success d-flex align-items-center gap-1 ms-auto"
                        onClick={handleSaveAll}
                        disabled={saving || Object.keys(editedUsers).length === 0}
                        title="변경된 내용 일괄 저장"
                      >
                        {saving
                          ? <span className="spinner-border spinner-border-sm" />
                          : <IconDeviceFloppy size={16} />}
                        저장{Object.keys(editedUsers).length > 0 && ` (${Object.keys(editedUsers).length})`}
                      </button>
                    </div>
                      </div>
                    </div>
                  </form>
                </div>

                <div className="table-responsive">
                  <table className="table table-vcenter table-hover card-table">
                    <thead>
                      <tr>
                        <th className="w-1">ID</th>
                        <th>아이디</th>
                        <th>이름</th>
                        <th>닉네임</th>
                        <th>역할</th>
                        <th>상태</th>
                        <th>최근 로그인</th>
                        <th>가입일</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={8} className="text-center py-5">
                            <div className="spinner-border text-primary" style={{ width: 40, height: 40 }} />
                          </td>
                        </tr>
                      ) : users.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center text-muted py-5">
                            검색 결과가 없습니다.
                          </td>
                        </tr>
                      ) : users.map((user) => {
                        const edits    = editedUsers[user.id] || {};
                        const isDirty  = Object.keys(edits).length > 0;
                        const curName  = edits.name   ?? user.name   ?? '';
                        const curRole  = edits.role   ?? user.role;
                        const curStatus = edits.status ?? user.status;
                        return (
                        <tr key={user.id}
                          style={{
                            ...(isDirty ? { background: 'rgba(var(--tblr-warning-rgb,255,193,7), 0.08)' } : {}),
                            ...((me?.role === 'MASTER' || me?.role === 'ADMIN') ? { cursor: 'pointer' } : {}),
                          }}
                          onClick={() => (me?.role === 'MASTER' || me?.role === 'ADMIN') && openUserDetail(user)}
                        >
                          <td><span className="text-muted">{user.id}</span></td>
                          <td>
                            <div className="d-flex align-items-center">
                              {user.profileImageThumb ? (
                                <img src={user.profileImageThumb} alt="" style={{
                                  width: 28, height: 28, borderRadius: '50%', objectFit: 'cover',
                                  flexShrink: 0, marginRight: 8,
                                }} />
                              ) : (
                                <span style={{
                                  background: '#206bc4', color: '#fff', borderRadius: '50%',
                                  width: 28, height: 28, display: 'inline-flex',
                                  alignItems: 'center', justifyContent: 'center',
                                  fontSize: 12, fontWeight: 600, flexShrink: 0, marginRight: 8,
                                }}>
                                  {curName?.[0] || user.email[0].toUpperCase()}
                                </span>
                              )}
                              {(() => { const c = isDark ? (user.darkColor || user.color) : user.color; return c ? <span style={{ color: c, fontWeight: 600 }}>{user.email}</span> : user.email; })()}
                            </div>
                          </td>
                          {/* 이름 — 읽기 전용 */}
                          <td>
                            {(() => { const c = isDark ? (user.darkColor || user.color) : user.color; return c ? <span style={{ color: c, fontWeight: 600 }}>{curName}</span> : curName; })()}
                          </td>
                          {/* 닉네임 — 읽기 전용 */}
                          <td>
                            {(() => { const c = isDark ? (user.darkColor || user.color) : user.color; return c
                              ? <span style={{ color: c, fontWeight: 600, fontSize: 13 }}>{user.nickname || <span style={{ opacity: 0.4 }}>-</span>}</span>
                              : <span className="text-muted" style={{ fontSize: 13 }}>{user.nickname || <span style={{ opacity: 0.4 }}>-</span>}</span>;
                            })()}
                          </td>
                          {/* 역할 — 저장 버튼 클릭 시 반영 */}
                          <td onClick={e => e.stopPropagation()}>
                            <select
                              className="form-select form-select-sm"
                              style={{ width: 110 }}
                              value={curRole}
                              onChange={(e) => updateUserField(user.id, 'role', e.target.value)}
                            >
                              <option value="USER">USER</option>
                              <option value="ADMIN">ADMIN</option>
                              {me?.role === 'MASTER' && (
                                <option value="MASTER">MASTER</option>
                              )}
                            </select>
                          </td>
                          {/* 상태 — 저장 버튼 클릭 시 반영 */}
                          <td onClick={e => e.stopPropagation()}>
                            <select
                              className={`form-select form-select-sm fw-semibold ${curStatus === 'ACTIVE' ? 'text-success' : curStatus === 'PENDING' ? 'text-warning' : 'text-danger'}`}
                              style={{ width: 90 }}
                              value={curStatus}
                              onChange={(e) => updateUserField(user.id, 'status', e.target.value)}
                            >
                              <option value="ACTIVE">● 활성</option>
                              <option value="PENDING">● 대기</option>
                              <option value="BLOCKED">● 차단</option>
                            </select>
                          </td>
                          <td className="text-muted small">{fmt(user.lastLoginAt)}</td>
                          <td className="text-muted small">{fmt(user.createdAt)}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* 페이징 */}
                {totalPages > 1 && (
                  <div className="card-footer d-flex align-items-center">
                    <p className="m-0 text-muted">
                      <span>{page * 15 + 1}</span>–
                      <span>{Math.min((page + 1) * 15, totalElements)}</span> /
                      전체 <span>{totalElements}</span>명
                    </p>
                    <ul className="pagination m-0 ms-auto">
                      <li className={`page-item${page === 0 ? ' disabled' : ''}`}>
                        <button className="page-link" onClick={() => fetchUsers(page - 1, keyword, filterStatus, filterRole)}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6" />
                          </svg>
                          이전
                        </button>
                      </li>
                      {[...Array(Math.min(totalPages, 7))].map((_, i) => {
                        const p = Math.max(0, Math.min(page - 3, totalPages - 7)) + i;
                        return (
                          <li key={p} className={`page-item${page === p ? ' active' : ''}`}>
                            <button className="page-link" onClick={() => fetchUsers(p, keyword, filterStatus, filterRole)}>
                              {p + 1}
                            </button>
                          </li>
                        );
                      })}
                      <li className={`page-item${page === totalPages - 1 ? ' disabled' : ''}`}>
                        <button className="page-link" onClick={() => fetchUsers(page + 1, keyword, filterStatus, filterRole)}>
                          다음
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        </div>

        {/* ── 탭 콘텐츠 (mountedTabs: 한 번 열린 탭은 display:none으로 유지) ── */}
        <div style={{ display: mountedTabs.has('roles') ? (activeTab === 'roles' ? 'block' : 'none') : 'none' }}>
          {mountedTabs.has('roles') && <RolePage me={me} showToast={showToast} />}
        </div>
        <div style={{ display: mountedTabs.has('menus') ? (activeTab === 'menus' ? 'block' : 'none') : 'none' }}>
          {mountedTabs.has('menus') && <MenuPage me={me} showToast={showToast} />}
        </div>
        <div style={{ display: mountedTabs.has('front-menus') ? (activeTab === 'front-menus' ? 'block' : 'none') : 'none' }}>
          {mountedTabs.has('front-menus') && <FrontMenuPage me={me} showToast={showToast} />}
        </div>
        <div style={{ display: mountedTabs.has('messages') ? (activeTab === 'messages' ? 'block' : 'none') : 'none' }}>
          {mountedTabs.has('messages') && <MessagePage me={me} showToast={showToast} onUnreadChange={fetchUnreadCount} editorType={editorType} />}
        </div>
        <div style={{ display: mountedTabs.has('schedules') ? (activeTab === 'schedules' ? 'block' : 'none') : 'none' }}>
          {mountedTabs.has('schedules') && <SchedulePage me={me} showToast={showToast} />}
        </div>
        <div style={{ display: mountedTabs.has('board-manage') ? (activeTab === 'board-manage' ? 'block' : 'none') : 'none' }}>
          {mountedTabs.has('board-manage') && <BoardManagePage me={me} showToast={showToast} onBoardsChange={fetchBoards} />}
        </div>
        <div style={{ display: mountedTabs.has('audit-logs') ? (activeTab === 'audit-logs' ? 'block' : 'none') : 'none' }}>
          {mountedTabs.has('audit-logs') && <AuditLogPage me={me} showToast={showToast} />}
        </div>
        <div style={{ display: mountedTabs.has('notifications') ? (activeTab === 'notifications' ? 'block' : 'none') : 'none' }}>
          {mountedTabs.has('notifications') && <NotificationPage me={me} showToast={showToast} />}
        </div>
        <div style={{ display: mountedTabs.has('files') ? (activeTab === 'files' ? 'block' : 'none') : 'none' }}>
          {mountedTabs.has('files') && <FileManagerPage me={me} showToast={showToast} />}
        </div>
        <div style={{ display: mountedTabs.has('api-keys') ? (activeTab === 'api-keys' ? 'block' : 'none') : 'none' }}>
          {mountedTabs.has('api-keys') && <ApiKeyManagePage me={me} showToast={showToast} />}
        </div>
        <div style={{ display: mountedTabs.has('access-controls') ? (activeTab === 'access-controls' ? 'block' : 'none') : 'none' }}>
          {mountedTabs.has('access-controls') && <AccessControlPage me={me} showToast={showToast} />}
        </div>
        <div style={{ display: mountedTabs.has('backups') ? (activeTab === 'backups' ? 'block' : 'none') : 'none' }}>
          {mountedTabs.has('backups') && <BackupPage me={me} showToast={showToast} />}
        </div>
        <div style={{ display: mountedTabs.has('system-config') ? (activeTab === 'system-config' ? 'block' : 'none') : 'none' }}>
          {mountedTabs.has('system-config') && <SystemConfigPage me={me} showToast={showToast} />}
        </div>
        <div style={{ display: mountedTabs.has('account-settings') ? (activeTab === 'account-settings' ? 'block' : 'none') : 'none' }}>
          {mountedTabs.has('account-settings') && <AccountSettingsPage me={me} showToast={showToast} onUpdated={(updatedUser) => setMe(updatedUser)} onEditorTypeChange={handleEditorTypeChange} />}
        </div>
        <div style={{ display: mountedTabs.has('golf-courses') ? (activeTab === 'golf-courses' ? 'block' : 'none') : 'none' }}>
          {mountedTabs.has('golf-courses') && <GolfCoursesPage me={me} showToast={showToast} />}
        </div>
        <div style={{ display: mountedTabs.has('golf-reservations') ? (activeTab === 'golf-reservations' ? 'block' : 'none') : 'none' }}>
          {mountedTabs.has('golf-reservations') && <GolfReservationsPage me={me} showToast={showToast} />}
        </div>
        <div style={{ display: mountedTabs.has('stock-ledger') ? (activeTab === 'stock-ledger' ? 'block' : 'none') : 'none' }}>
          {mountedTabs.has('stock-ledger') && <StockLedgerPage me={me} showToast={showToast} />}
        </div>
        <div style={{ display: mountedTabs.has('market-analysis') ? (activeTab === 'market-analysis' ? 'block' : 'none') : 'none' }}>
          {mountedTabs.has('market-analysis') && <MarketAnalysisPage me={me} showToast={showToast} />}
        </div>
        <div style={{ display: mountedTabs.has('krx-data') ? (activeTab === 'krx-data' ? 'block' : 'none') : 'none' }}>
          {mountedTabs.has('krx-data') && <KrxDataPage me={me} showToast={showToast} />}
        </div>

        {/* ── 게시판 탭 콘텐츠 ── */}
        {tabs.filter(t => t.id.startsWith('board-') && t.id !== 'board-manage').map(t => {
          const boardId = parseInt(t.id.replace('board-', ''), 10);
          const board = openBoards[boardId];
          if (!board || !mountedTabs.has(t.id)) return null;
          return (
            <div key={`${boardId}-${board.initialPostId||0}`} style={{ display: activeTab === t.id ? 'block' : 'none' }}>
              <BoardPage board={board} me={me} showToast={showToast} initialPostId={board.initialPostId} editorType={editorType} />
            </div>
          );
        })}

        {/* 테마 설정 패널 */}
        <ThemeSettings />

        {/* 프로필 수정 모달 */}
        {profileModalOpen && (
          <ProfileModal
            me={me}
            onClose={() => setProfileModalOpen(false)}
            onUpdated={(updatedUser) => setMe(updatedUser)}
          />
        )}

        {/* ── 사용자 상세 모달 (MASTER) ── */}
        {userDetailOpen && userDetailTarget && (
          <div className="modal modal-blur show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.4)' }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 440 }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">사용자 정보 수정</h5>
                  <button className="btn-close" onClick={() => setUserDetailOpen(false)} />
                </div>
                <div className="modal-body">
                  {/* 프로필 사진 */}
                  <div className="text-center mb-3">
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      {userDetailForm.profileImage ? (
                        <img src={userDetailForm.profileImage} alt="프로필"
                          style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e6e7e9' }}
                        />
                      ) : (
                        <span style={{
                          width: 80, height: 80, borderRadius: '50%', background: '#206bc4', color: '#fff',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 28, fontWeight: 600,
                        }}>
                          {(userDetailTarget.name || userDetailTarget.email)[0].toUpperCase()}
                        </span>
                      )}
                      <label style={{
                        position: 'absolute', bottom: 0, right: 0,
                        background: '#206bc4', color: '#fff', borderRadius: '50%',
                        width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', border: '2px solid #fff',
                      }}>
                        <IconCamera size={13} />
                        <input type="file" accept="image/*" style={{ display: 'none' }}
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (file.size > 2 * 1024 * 1024) { showToast('2MB 이하만 가능합니다.', 'danger'); return; }
                            const reader = new FileReader();
                            reader.onload = ev => setUserDetailForm(f => ({ ...f, profileImage: ev.target.result }));
                            reader.readAsDataURL(file);
                          }}
                        />
                      </label>
                    </div>
                    {userDetailForm.profileImage && (
                      <button className="btn btn-sm btn-ghost-danger mt-1 d-block mx-auto"
                        onClick={() => setUserDetailForm(f => ({ ...f, profileImage: null }))}>
                        <IconTrash size={14} className="me-1" />사진 삭제
                      </button>
                    )}
                  </div>

                  {/* 아이디 (읽기 전용) */}
                  <div className="mb-3">
                    <label className="form-label">아이디</label>
                    <input className="form-control form-control-sm" value={userDetailTarget.email} disabled
                      style={{ background: 'var(--tblr-bg-surface-secondary, #f4f6fa)' }} />
                  </div>

                  {/* 이름 */}
                  <div className="mb-3">
                    <label className="form-label">이름</label>
                    <input className="form-control form-control-sm" value={userDetailForm.name} disabled
                      style={{ background: 'var(--tblr-bg-surface-secondary, #f4f6fa)' }} />
                  </div>

                  {/* 닉네임 */}
                  <div className="mb-3">
                    <label className="form-label">닉네임
                      <span className="text-muted fw-normal ms-1" style={{ fontSize: 12 }}>(없으면 이름으로 표시)</span>
                    </label>
                    <input className="form-control form-control-sm" placeholder="닉네임 입력"
                      value={userDetailForm.nickname}
                      onChange={e => setUserDetailForm(f => ({ ...f, nickname: e.target.value }))}
                    />
                  </div>

                  {/* 글씨색 (라이트/다크) */}
                  <div className="mb-3">
                    <label className="form-label">글씨색</label>
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <div className="d-flex align-items-center gap-1">
                        <span style={{ fontSize: 11, color: '#888', width: 40 }}>라이트</span>
                        <input type="color" value={userDetailForm.color || '#000000'}
                          onChange={e => setUserDetailForm(f => ({ ...f, color: e.target.value }))}
                          style={{ width: 32, height: 28, padding: 0, border: '1px solid #dee2e6', borderRadius: 4, cursor: 'pointer' }} />
                        <span style={{ fontSize: 11, color: userDetailForm.color || '#888' }}>{userDetailForm.color || '없음'}</span>
                        {userDetailForm.color && (
                          <button className="btn btn-sm btn-ghost-secondary" style={{ padding: '0 4px', fontSize: 10 }}
                            onClick={() => setUserDetailForm(f => ({ ...f, color: '' }))}>✕</button>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-1">
                        <span style={{ fontSize: 11, color: '#888', width: 40 }}>다크</span>
                        <input type="color" value={userDetailForm.darkColor || '#ffffff'}
                          onChange={e => setUserDetailForm(f => ({ ...f, darkColor: e.target.value }))}
                          style={{ width: 32, height: 28, padding: 0, border: '1px solid #dee2e6', borderRadius: 4, cursor: 'pointer' }} />
                        <span style={{ fontSize: 11, color: '#888' }}>{userDetailForm.darkColor || '없음'}</span>
                        {userDetailForm.darkColor && (
                          <button className="btn btn-sm btn-ghost-secondary" style={{ padding: '0 4px', fontSize: 10 }}
                            onClick={() => setUserDetailForm(f => ({ ...f, darkColor: '' }))}>✕</button>
                        )}
                      </div>
                      {(userDetailForm.color || userDetailForm.darkColor) && (
                        <div className="d-flex gap-1">
                          <span style={{ color: userDetailForm.color || '#333', background: '#fff', border: '1px solid #dee2e6', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>밝은배경</span>
                          <span style={{ color: userDetailForm.darkColor || '#ccc', background: '#1e293b', border: '1px solid #334155', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>어두운배경</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 비밀번호 */}
                  <div className="mb-3">
                    <label className="form-label">비밀번호 변경 <span className="text-muted">(입력 시 변경)</span></label>
                    <input type="password" className="form-control form-control-sm" placeholder="새 비밀번호"
                      value={userDetailForm.password}
                      onChange={e => setUserDetailForm(f => ({ ...f, password: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="d-flex gap-1 w-100 flex-wrap">
                    {/* MASTER로그인시 ADMIN/MASTER대상, 또는 ADMIN로그인시 ADMIN대상 => admin+user 2개버튼 */}
                    {((me?.role === 'MASTER' && (userDetailTarget.role === 'ADMIN' || userDetailTarget.role === 'MASTER')) ||
                      (me?.role === 'ADMIN' && userDetailTarget.role === 'ADMIN')) ? (
                      <>
                        <button className="btn btn-sm btn-primary" disabled={impersonateLoading} onClick={() => {
                          if (window.confirm(`${userDetailTarget.name || userDetailTarget.email}로 Admin 로그인합니다.\n현재 세션이 종료됩니다.`)) {
                            handleImpersonate(userDetailTarget.id, `${window.location.origin}`);
                          }
                        }}>
                          {impersonateLoading ? (
                            <><span className="spinner-border spinner-border-sm me-2" style={{width:14, height:14}} /> 로그인 중...</>
                          ) : (
                            <><IconShield size={14} className="me-1" />Admin</>
                          )}
                        </button>
                        <button className="btn btn-sm btn-info" disabled={impersonateLoading} onClick={() => {
                          if (window.confirm(`${userDetailTarget.name || userDetailTarget.email}로 User 로그인합니다.\n현재 세션이 종료됩니다.`)) {
                            handleImpersonate(userDetailTarget.id, USER_WEB_URL, true);
                          }
                        }}>
                          {impersonateLoading ? (
                            <><span className="spinner-border spinner-border-sm me-2" style={{width:14, height:14}} /> 로그인 중...</>
                          ) : (
                            <><IconUser size={14} className="me-1" />User</>
                          )}
                        </button>
                      </>
                    ) : (
                      <button className="btn btn-sm btn-info" disabled={impersonateLoading} onClick={() => {
                        if (window.confirm(`${userDetailTarget.name || userDetailTarget.email}로 User 로그인합니다.\n현재 세션이 종료됩니다.`)) {
                          handleImpersonate(userDetailTarget.id, USER_WEB_URL, true);
                        }
                      }}>
                        {impersonateLoading ? (
                          <><span className="spinner-border spinner-border-sm me-2" style={{width:14, height:14}} /> 로그인 중...</>
                        ) : (
                          <><IconUser size={14} className="me-1" />User</>
                        )}
                      </button>
                    )}
                    <button className="btn btn-sm btn-outline-warning ms-auto" onClick={async () => {
                      if (!window.confirm(`${userDetailTarget.name || userDetailTarget.email}의 비밀번호를 초기화하시겠습니까?`)) return;
                      try {
                        const res = await api.post(`/admin/users/${userDetailTarget.id}/reset-password`);
                        const pw = res.data.tempPassword;
                        const copied = await navigator.clipboard.writeText(pw).then(() => true).catch(() => false);
                        showToast(`임시 비밀번호: ${pw}${copied ? ' (클립보드에 복사됨)' : ''}`);
                      } catch (err) {
                        showToast(err.response?.data?.message || '비밀번호 초기화 실패', 'error');
                      }
                    }}>
                      <IconLock size={14} className="me-1" />비번 초기화
                    </button>
                    <button className="btn btn-sm btn-primary" onClick={handleUserDetailSave} disabled={userDetailSaving}>
                      {userDetailSaving ? '저장 중...' : '저장'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 푸터 */}
        <footer className="footer footer-transparent d-print-none">
          <div className="container-xl">
            <div className="row text-center align-items-center flex-row-reverse">
              <div className="col-lg-auto ms-lg-auto">
                <ul className="list-inline list-inline-dots mb-0">
                  <li className="list-inline-item">
                    Copyright © {new Date().getFullYear()} <strong>{siteConfig['site.name']}</strong>. All rights reserved.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </footer>

      </div>
    </div>
  );
}
